import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, SHAP_TYPES } from '~/utils/constants/common';
import { cast, handleApiError, isWard, isXaphuongTinhThanh, notify } from '~/utils/helper';
import { CloseIcon, MarkerIcon } from '~/assets/icons';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { IMasterDataItem } from '~/utils/interface/master';
import locationApi from '~/apis/location';
import Map, { Layer, Marker, Source } from 'react-map-gl/mapbox';
import locationValidation from '~/validation/location';
import axios from 'axios';
import { MapRef } from 'react-map-gl/mapbox';
import { useAppDispatch } from '~/redux/hooks';
import { IEditLocation, ILocation, IPoint } from '~/utils/interface/location';
import { commonActions } from '~/thunks/common/commonSlice';
import mapboxgl from 'mapbox-gl';
import { LocationTypeEnum } from '~/utils/enum';
import i18n from '~/utils/i18n';
import { IUser } from '~/utils/interface/user';
import * as turf from '@turf/turf';

type EditLocationDialogProps = {
  data?: ILocation;
  open: boolean;
  mode: string;
  currentUser?: IUser;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
};

type InteractionMode = 'idle' | 'drawing' | 'moving' | 'resizing';

const initialValues: IEditLocation = {
  name: '',
  ward_id: '',
  province_id: '',
  radius: '',
  type: '',
  location_type: 0,
  points: [],
};

const EditLocationDialog = (props: EditLocationDialogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/location');

  const [provinceOptions, setProvinceOptions] = useState<IMasterDataItem[]>([]);
  const [wardOptions, setWardOptions] = useState<IMasterDataItem[]>([]);

  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  const [marker, setMarker] = useState<IPoint | null>(null);
  const [circleRadius, setCircleRadius] = useState<number | undefined>(undefined);

  const [fullAddress, setFullAddress] = useState<string>('');
  const [isPolygonStarted, setIsPolygonStarted] = useState(false);

  const [interactionMode, setInteractionMode] = useState<InteractionMode>('idle');
  const [drawStart, setDrawStart] = useState<IPoint | null>(null);
  const isDragging = useRef<boolean>(false);

  const mapRef = useRef<MapRef | null>(null);

  const locationTypeOptions = [
    {
      id: LocationTypeEnum.VAO,
      name: t('location_type.' + LocationTypeEnum.VAO),
    },
    {
      id: LocationTypeEnum.RA,
      name: t('location_type.' + LocationTypeEnum.RA),
    },
    {
      id: LocationTypeEnum.VAO_RA,
      name: t('location_type.' + LocationTypeEnum.VAO_RA),
    },
  ];

  const typeOptions = [
    {
      id: SHAP_TYPES.circle,
      name: t('type.circle'),
    },
    {
      id: SHAP_TYPES.polygon,
      name: t('type.polygon'),
    },
  ];

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    enableReinitialize: true,
    validationSchema: locationValidation(t).edit,
    onSubmit: async (values) => {
      dispatch(commonActions.setIsLoading(true));
      if (props.mode === DIALOG_MODE_CREATE) {
        try {
          await locationApi.create(values);
          notify('success', t('addSuccess', { ns: 'admin/common', name: t('location') }));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e, true);
        }
      }

      if (props.mode === DIALOG_MODE_EDIT && props.data?.id) {
        try {
          await locationApi.update(props.data?.id, values);
          notify('success', t('updateSuccess', { ns: 'admin/common', name: t('location') }));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e, true);
        }
      }
      dispatch(commonActions.setIsLoading(false));
    },
  });

  useEffect(() => {
    getListProvinces();
  }, [props.open]);

  useEffect(() => {
    if (form.values.province_id) {
      (async () => {
        try {
          const response = await locationApi.getListWards({ province: form.values.province_id ?? '' });
          setWardOptions(
            response.data.map((item) => {
              return {
                id: item.code,
                name: item.name,
                data0: item.name,
                data1: item.name_with_type,
                data2: item.type,
                data3: item.parent_code,
              };
            })
          );
        } catch (error) {
          setWardOptions([]);
          handleApiError(error);
        }
      })();
    } else setWardOptions([]);
  }, [form.values.province_id]);

  useEffect(() => {
    if (props.mode !== DIALOG_MODE_CREATE) return;

    form.setFieldValue('points', []);

    setMarker(null);
    setCircleRadius(undefined);
    setIsPolygonStarted(false);

    if (form.values.type === SHAP_TYPES.circle && marker) {
      form.setFieldValue('points', [marker]);
    }

    if (form.values.type === SHAP_TYPES.polygon) {
      setIsPolygonStarted(false);
      setMarker(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.type, props.mode]);

  useEffect(() => {
    if (isXaphuongTinhThanh(cast(props.currentUser?.roleid)))
      form.setFieldValue('province_id', String(props?.currentUser?.province_id));
    form.setFieldValue('ward_id', String(props?.currentUser?.ward_id || ''));

    if (!props.open || !props.data || props.mode !== DIALOG_MODE_EDIT) return;

    const data = props.data;
    const points = (data.points || []).map((p) => ({
      lng: Number(p.lng),
      lat: Number(p.lat),
    }));

    form.setValues(
      {
        name: cast(data.name),
        province_id: data.province_id ? String(data.province_id) : '',
        ward_id: data.ward_id ? String(data.ward_id) : '',
        radius: cast(data.radius),
        type: cast(data.type),
        location_type: data.location_type || 0,
        points,
      },
      false
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.data, props.mode]);

  useEffect(() => {
    if (props.open && props.data && props.mode === DIALOG_MODE_EDIT) {
      (async () => {
        const data = props.data!;
        const points = data.points || [];

        if (!points.length) return;

        const first = points[0];
        setMarker(first);

        if (data.type === SHAP_TYPES.circle && data.radius) {
          setCircleRadius(Number(data.radius));
        }

        const address = await buildFullAddress(first.lng, first.lat);
        setFullAddress(address);

        const map = mapRef.current?.getMap();
        if (!map) return;

        if (points.length === 1) {
          map.flyTo({
            center: [first.lng, first.lat],
            zoom: 13,
          });
        } else {
          const bounds = new mapboxgl.LngLatBounds();
          points.forEach((p) => bounds.extend([p.lng, p.lat]));
          map.fitBounds(bounds, { padding: 80, duration: 1000 });
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.data, props.mode, mapLoaded]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (props.mode === DIALOG_MODE_EDIT) return;
      if (!form.values.province_id) return;

      const province = provinceOptions.find((p) => p.id === form.values.province_id)?.data0;
      if (!province) return;

      const ward = wardOptions.find((w) => w.id === form.values.ward_id)?.data0;

      const address = ward ? `${ward}, ${province}` : province;

      try {
        const coords = await geocodeAddress(address);
        if (!coords || cancelled) return;

        setMarker(coords);
        form.setFieldValue('points', [coords]);

        mapRef.current?.flyTo({
          center: [coords.lng, coords.lat],
          zoom: 13,
        });

        setFullAddress(address);
      } catch (err) {
        handleApiError(err);
      }
    };

    run();
    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.province_id, form.values.ward_id, provinceOptions, wardOptions]);

  const handleMapPress = async (event: any) => {
    if (!form.values.type || isDragging.current) return;

    const { lng, lat } = event.lngLat;
    const newPoint = { lng, lat };

    if (form.values.type === SHAP_TYPES.circle) {
      form.setFieldValue('points', [newPoint]);
      setMarker(newPoint);

      const address = await buildFullAddress(lng, lat);
      setFullAddress(address);
      return;
    }

    if (form.values.type === SHAP_TYPES.polygon) {
      if (!isPolygonStarted) {
        setIsPolygonStarted(true);
        form.setFieldValue('points', [newPoint]);
      } else {
        form.setFieldValue('points', [...(form.values.points || []), newPoint]);
      }

      const address = await buildFullAddress(lng, lat);
      setFullAddress(address);
    }
  };

  const buildFullAddress = async (lng: number, lat: number) => {
    const geo = await reverseGeocode(lng, lat);

    return Array.from(new Set([...geo].filter(Boolean))).join(', ');
  };

  const reverseGeocode = async (lng: number, lat: number) => {
    const res = await axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json`, {
      params: {
        access_token: process.env.REACT_APP_MAP_BOX_ACCESS_TOKEN ?? '',
        language: i18n.language,
      },
    });

    if (!res.data.features?.length) return [];

    const filteredPlaceName = res.data.features[0].place_name
      .split(',')
      .map((part: any) => part.trim())
      .filter((part: any) => !/^\d{4,}$/.test(part));

    return filteredPlaceName.slice(0, filteredPlaceName.length - 3);
  };

  const getListProvinces = async () => {
    try {
      const response = await locationApi.getListProvinces();
      setProvinceOptions(
        response.data.map((item) => {
          return {
            id: item.code,
            name: item.name,
            data0: item.name,
            data1: item.name_with_type,
            data2: item.type,
          };
        })
      );
    } catch (error) {
      handleApiError(error);
    }
  };

  const renderPointRadius = () => {
    const centerPoint = form.values.points?.[0] ?? marker;

    if (form.values.type === SHAP_TYPES.circle && centerPoint && circleRadius) {
      const radiusInKm = circleRadius / 1000;
      const circleGeoJson = turf.circle([centerPoint.lng, centerPoint.lat], radiusInKm, {
        steps: 64,
        units: 'kilometers',
      });

      return (
        <Source id='circle-source' type='geojson' data={circleGeoJson as any}>
          <Layer
            id='circle-fill'
            type='fill'
            paint={{
              'fill-color': 'rgba(66, 135, 245, 0.3)',
            }}
          />

          <Layer
            id='circle-outline'
            type='line'
            paint={{
              'line-color': 'rgba(66, 135, 245, 0.8)',
              'line-width': 2,
            }}
          />
        </Source>
      );
    }

    return null;
  };

  const renderPolygon = () => {
    if (form.values.type !== SHAP_TYPES.polygon) return null;

    const points = form.values.points || [];

    const coords = points
      .filter((p): p is IPoint => typeof p.lng === 'number' && typeof p.lat === 'number')
      .map((p) => [p.lng, p.lat]);

    if (coords.length < 2) return null;

    const polygon: any =
      coords.length >= 3
        ? {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [[...coords, coords[0]]],
            },
            properties: {},
          }
        : null;

    const lineString: any = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: coords,
      },
      properties: {},
    };

    return (
      <>
        <Source id='polygon-line-source' type='geojson' data={lineString}>
          <Layer
            id='polygon-line'
            type='line'
            paint={{
              'line-color': '#4287f5',
              'line-width': 2,
            }}
          />
        </Source>

        {polygon && (
          <Source id='polygon-fill-source' type='geojson' data={polygon}>
            <Layer
              id='polygon-fill'
              type='fill'
              paint={{
                'fill-color': 'rgba(66, 135, 245, 0.3)',
                'fill-outline-color': '#4287f5',
              }}
            />
          </Source>
        )}
      </>
    );
  };

  const geocodeAddress = async (address: string) => {
    try {
      const res = await axios.get(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json`,
        {
          params: {
            access_token: process.env.REACT_APP_MAP_BOX_ACCESS_TOKEN ?? '',
            limit: 1,
          },
        }
      );

      const feature = res.data.features?.[0];
      if (!feature) return null;

      const [lng, lat] = feature.center;
      return { lng, lat };
    } catch (err) {
      throw err;
    }
  };

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (!open) {
      setMarker(null);
    }
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const getDistanceKm = (a: IPoint, b: IPoint) => {
    return turf.distance([a.lng, a.lat], [b.lng, b.lat], { units: 'kilometers' });
  };

  const isNearCircleEdge = (cursor: IPoint) => {
    if (!marker || !circleRadius) return false;

    const distKm = getDistanceKm(cursor, marker);
    const radiusKm = circleRadius / 1000;

    return Math.abs(distKm - radiusKm) < radiusKm * 0.15;
  };

  const isInsideCircle = (cursor: IPoint) => {
    if (!marker || !circleRadius) return false;

    const distKm = getDistanceKm(cursor, marker);

    return distKm < (circleRadius / 1000) * 0.85;
  };

  const handleMouseDown = (event: any) => {
    if (form.values.type === SHAP_TYPES.polygon || !marker) return;

    const { lng, lat } = event.lngLat;
    const cursor = { lng, lat };

    if (circleRadius) {
      if (isNearCircleEdge(cursor)) {
        isDragging.current = true;
        setInteractionMode('resizing');
        mapRef.current?.getMap()?.dragPan.disable();
      } else if (isInsideCircle(cursor)) {
        isDragging.current = true;
        setInteractionMode('moving');
        setDrawStart(cursor);
        mapRef.current?.getMap()?.dragPan.disable();
      }
    } else {
      isDragging.current = true;
      setInteractionMode('drawing');
      setDrawStart(cursor);
      setMarker(cursor);
      setCircleRadius(0);
      form.setFieldValue('points', [cursor]);
      mapRef.current?.getMap()?.dragPan.disable();
    }
  };

  const handleMouseMove = (event: any) => {
    if (form.values.type === SHAP_TYPES.polygon || !isDragging.current) return;

    const { lng, lat } = event.lngLat;
    const cursor = { lng, lat };

    if (interactionMode === 'drawing' && drawStart) {
      const distKm = getDistanceKm(drawStart, cursor);
      const distM = distKm * 1000;
      setCircleRadius(distM);
      form.setFieldValue('radius', Math.round(distM).toString());
    }

    if (interactionMode === 'resizing' && marker) {
      const distKm = getDistanceKm(marker, cursor);
      const distM = distKm * 1000;
      setCircleRadius(distM);
      form.setFieldValue('radius', Math.round(distM).toString());
    }

    if (interactionMode === 'moving' && marker && drawStart) {
      const deltaLng = cursor.lng - drawStart.lng;
      const deltaLat = cursor.lat - drawStart.lat;
      const newCenter = { lng: marker.lng + deltaLng, lat: marker.lat + deltaLat };
      setMarker(newCenter);
      form.setFieldValue('points', [newCenter]);
      setDrawStart(cursor);
    }
  };

  const handleMouseUp = async () => {
    if (!isDragging.current) return;

    setTimeout(() => {
      isDragging.current = false;
    }, 0);

    setTimeout(() => {
      mapRef.current?.getMap()?.dragPan.enable();
    }, 0);

    setInteractionMode('idle');

    if (marker && (interactionMode === 'drawing' || interactionMode === 'moving')) {
      const address = await buildFullAddress(marker.lng, marker.lat);
      setFullAddress(address);
    }
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='sm'>
      <DialogTitle className='bg-white text-xl font-semibold text-[#161A1F]'>
        {props.mode === DIALOG_MODE_EDIT ? t('edit') : t('create')}
        <IconButton className='p-1' onClick={() => handleSetOpen(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography className='text-[#161A1F] font-medium text-sm mb-3'>
          {t('province_ward_id')}: {fullAddress}
        </Typography>
        <Box component='form'>
          <Box className='mb-2'>
            <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('province_id')}</InputLabel>
            <BaseSelect
              options={
                isXaphuongTinhThanh(cast(props.currentUser?.roleid))
                  ? provinceOptions.filter(
                      (item: IMasterDataItem) => item.id === String(props.currentUser?.province_id)
                    )
                  : provinceOptions
              }
              value={form.values.province_id}
              placeholder={t('select_province_id')}
              name='province_id'
              isSearchEnable
              onChange={form.handleChange}
              helperText={form.errors?.province_id}
              disabled={isXaphuongTinhThanh(cast(props.currentUser?.roleid))}
            />
          </Box>
          <Box className='mb-2'>
            <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('ward_id')}</InputLabel>
            <BaseSelect
              options={wardOptions}
              value={form.values.ward_id}
              placeholder={t('select_ward_id')}
              name='ward_id'
              isSearchEnable
              onChange={form.handleChange}
              helperText={form.errors?.ward_id}
              disabled={isWard(cast(props.currentUser?.roleid))}
            />
          </Box>
          <Box className='mb-2'>
            <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('name')}</InputLabel>
            <TextField
              autoComplete='off'
              size='medium'
              className='w-full rounded-xl'
              name='name'
              placeholder={t('enterName')}
              value={form.values?.name ?? ''}
              onChange={form.handleChange}
              error={Boolean(form.errors?.name)}
              helperText={form.errors?.name}
              FormHelperTextProps={{
                style: { fontSize: '12px', lineHeight: '16px', marginLeft: 0, marginRight: 0 },
              }}
            />
          </Box>
          <Box className='mb-2'>
            <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('type_location')}</InputLabel>
            <BaseSelect
              options={locationTypeOptions}
              value={form.values.location_type ?? 0}
              placeholder={t('select_type_location')}
              name='location_type'
              isSearchEnable
              isDisplayAllOption
              onChange={form.handleChange}
              helperText={form.errors?.location_type}
            />
          </Box>
          <Box className='mb-3'>
            <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('type.title')}</InputLabel>
            <BaseSelect
              options={typeOptions}
              value={form.values.type ?? ''}
              placeholder={t('select_type')}
              name='type'
              isSearchEnable
              isDisplayAllOption
              onChange={(e: any) => {
                form.setValues({
                  ...form.values,
                  type: e.target.value,
                  radius: '',
                  points: [],
                });
              }}
              helperText={form.errors?.type}
            />
          </Box>
          {form.values.type === SHAP_TYPES.circle && (
            <Box className='mb-3'>
              <InputLabel className='text-[#161A1F] font-medium text-sm mb-2'>{t('radius')}</InputLabel>
              <TextField
                name='radius'
                placeholder={t('radius_value')}
                value={form.values?.radius ?? ''}
                onChange={(e: any) => {
                  form.handleChange(e);
                  const value = Number(e.target.value);
                  setCircleRadius(isNaN(value) ? undefined : value);
                }}
                InputProps={{
                  endAdornment: <InputAdornment position='end'>m</InputAdornment>,
                }}
                className='w-full'
                error={Boolean(form.errors?.radius)}
                helperText={form.errors?.radius}
                FormHelperTextProps={{
                  sx: {
                    fontSize: '0.75rem',
                    lineHeight: '1rem',
                    margin: '2px 0 0',
                  },
                }}
              />
            </Box>
          )}

          <Box className='mt-5 relative'>
            {form.errors.points && typeof form.errors.points === 'string' && (
              <Box className='text-[#d32f2f] text-xs mb-2'>{form.errors.points}</Box>
            )}

            {(!form.values.type || !form.values.name || !form.values.province_id) && (
              <Box className='absolute inset-0 z-10 bg-[#DCE8F5] text-[#d32f2f] flex items-center justify-center text-base opacity-70'></Box>
            )}

            <Map
              ref={mapRef}
              onLoad={() => setMapLoaded(true)}
              onClick={handleMapPress}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              mapboxAccessToken={process.env.REACT_APP_MAP_BOX_ACCESS_TOKEN}
              initialViewState={{
                longitude: 105.85,
                latitude: 21.02,
                zoom: 6,
              }}
              style={{ width: '100%', height: 400, marginBottom: 20 }}
              mapStyle='mapbox://styles/mapbox/streets-v12'
            >
              {form.values.points?.map((point: any, index: any) => (
                <Marker key={index} longitude={point.lng} latitude={point.lat}>
                  <MarkerIcon />
                </Marker>
              ))}

              {renderPointRadius()}
              {renderPolygon()}
            </Map>
          </Box>
          <Box className='w-full'>
            <Box className='flex justify-between gap-2'>
              <Button
                variant='outlined'
                className='w-full bg-[#D7DEE7] border-0 !border-inherit text-[#404854] text-base font-semibold'
                onClick={() => handleSetOpen(false)}
              >
                {t('cancel', { ns: 'admin/common' })}
              </Button>
              <Button
                variant='contained'
                className='w-full bg-[#123B6D] text-base font-semibold'
                onClick={() => form.handleSubmit()}
              >
                {props.mode === DIALOG_MODE_CREATE ? t('create') : t('save')}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default memo(EditLocationDialog);
